"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="221721" className="flex gap-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-9 w-20 animate-pulse rounded-none bg-muted" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-sm font-default font-semibold uppercase tracking-wider text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute inset-x-4 bottom-0 h-[2px] origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="mt-8 flex flex-col gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-10 w-full animate-pulse rounded-none bg-muted" />)}
      </div>;
  }
  return <div className="mt-8 flex flex-col gap-2">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block rounded-none px-4 py-3 text-lg font-default font-bold uppercase tracking-wide text-foreground transition-colors hover:bg-accent hover:text-primary">
            {item.label}
          </Link>
        </div>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="navegación-principal" className={`sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-background/80 border-b border-border shadow-lg backdrop-blur-md" : "bg-background border-b border-border/20"}`}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6 lg:px-8">
        {}
        <div className="flex shrink-0 items-center">
          <Link to="/" className="flex items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647820/img/arrels_industrial_logo.png" alt="ARRELS Logo" className="h-8 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden flex-1 justify-center md:flex">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex shrink-0 items-center gap-4">
          <Button asChild className="group relative hidden overflow-hidden md:flex rounded-none bg-primary text-primary-foreground font-default font-bold uppercase tracking-widest shadow-md hover:shadow-xl transition-all duration-300" size="lg">
            <Link to="https://maquinaarrels.com/contacto/">
              <span className="absolute right-2 top-2 flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-foreground opacity-75"></span>
                <span className="relative inline-flex size-2 rounded-full bg-primary-foreground"></span>
              </span>
              Cotizar
            </Link>
          </Button>

          {}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden rounded-none" aria-label="Menü öffnen">
                <Menu className="size-6 text-foreground" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="flex w-[300px] flex-col sm:w-[400px] bg-background border-l border-border">
              <SheetHeader className="px-0 text-left">
                <Link to="/" className="inline-block">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647820/img/arrels_industrial_logo.png" alt="ARRELS Logo" className="h-8 w-auto object-contain" />
                </Link>
              </SheetHeader>
              
              <div className="flex-1 overflow-y-auto">
                <MenuProvider menu_id="16">
                  <MobileMenuConsumer />
                </MenuProvider>
              </div>

              <div className="mt-auto pt-6">
                <Button asChild className="w-full rounded-none bg-primary text-primary-foreground font-default font-bold uppercase tracking-widest" size="lg">
                  <Link to="https://maquinaarrels.com/contacto/">
                    Cotizar Maquinaria
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}